import React, { useState } from 'react';
import { Plane, PartyPopper, Loader2, CheckCircle } from 'lucide-react';

const LABELS = { reschedule: 'Move after trip', pause: 'Pause', keep: 'Keep', remove: 'Remove' };
const KIND_LABEL = { task: 'Task', non_negotiable: 'Routine', calendar: 'Event' };

const ItemRow = ({ item, value, onChange }) => (
  <div className="py-2 border-b border-white/5 last:border-0" data-testid={`trip-item-${item.id}`}>
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-xs font-medium text-white truncate">{item.title}</p>
      <span className="text-[10px] text-gray-500 flex-shrink-0">{KIND_LABEL[item.kind]}</span>
    </div>
    <p className="text-[10px] text-gray-400 mb-1.5">{item.when}{item.reminder ? ' · reminder' : ''}</p>
    <div className="flex gap-1">
      {item.options.map(opt => (
        <button key={opt} onClick={() => onChange(opt)} data-testid={`trip-item-${item.id}-${opt}`}
          className="text-[10px] px-2 py-0.5 rounded-full transition-colors"
          style={value === opt
            ? { background: '#00D9FF', color: '#0A0E27' }
            : { background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)' }}>
          {LABELS[opt]}
        </button>
      ))}
    </div>
  </div>
);

const TripReviewCard = ({ action, onApply }) => {
  const { trip, items = [] } = action;
  const [choices, setChoices] = useState(() => Object.fromEntries(items.map(i => [i.id, i.suggested])));
  const [state, setState] = useState(action.applied ? 'done' : 'idle');

  const apply = async () => {
    setState('saving');
    const ok = await onApply(trip, items.map(i => ({ id: i.id, kind: i.kind, action: choices[i.id] })));
    setState(ok ? 'done' : 'idle');
  };

  return (
    <div className="mt-2 rounded-xl p-3 text-left" data-testid="trip-review-card"
      style={{ background: 'rgba(0,217,255,0.05)', border: '1px solid rgba(0,217,255,0.2)' }}>
      <div className="flex items-center gap-2 mb-2">
        {trip.kind === 'event' ? <PartyPopper size={13} className="text-cyan-400" /> : <Plane size={13} className="text-cyan-400" />}
        <p className="text-xs font-semibold text-cyan-300" data-testid="trip-review-label">{trip.label}</p>
      </div>
      {items.length > 0 && (
        <div className="max-h-48 overflow-y-auto pr-1">
          {items.map(i => (
            <ItemRow key={i.id} item={i} value={choices[i.id]}
              onChange={v => setChoices(c => ({ ...c, [i.id]: v }))} />
          ))}
        </div>
      )}
      {state === 'done' ? (
        <p className="mt-2 text-[11px] text-green-400 flex items-center gap-1" data-testid="trip-applied-msg">
          <CheckCircle size={12} /> Applied — your calendar is updated.
        </p>
      ) : (
        <button onClick={apply} disabled={state === 'saving'} data-testid="trip-apply-btn"
          className="mt-2 w-full text-xs font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #00D9FF, #0EA5E9)', color: '#0A0E27' }}>
          {state === 'saving' && <Loader2 size={12} className="animate-spin" />}
          {trip.kind === 'event' ? 'Apply & protect this event' : 'Apply & block these dates'}
        </button>
      )}
    </div>
  );
};

export default TripReviewCard;
